import React, { useImperativeHandle, forwardRef, useState } from 'react';

import { withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { green } from '@material-ui/core/colors';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import { useSnackbar } from 'notistack';

import { IntegerInput, TextInput } from '../../Components';
import { getListAllAPI, updateAPI } from '../../services';
import { SECONDARY_COLOR } from '../../utils';

const useStyles = makeStyles((theme) => ({
  rootDialogo: {
    borderRadius: '25px',
  },
  btnAtions: {
    borderRadius: '25px',
    width: '150px',
  },
  btnAtionsDisabled: {
    borderRadius: '25px',
    width: '150px',
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'white',
  },
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle
      disableTypography
      className={classes.root}
      {...other}
      style={{ backgroundColor: SECONDARY_COLOR }}
    >
      <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white' }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DEFAULT_ID = -1;
// espelha server/app/Utils/constants.js: SERIE_NFE_TESTE
const SERIE_NFE_TESTE = 9;

const DialogoTrocoInicial = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [carregando, setCarregando] = useState(false);
  const classes = useStyles();
  const [id, setId] = useState(DEFAULT_ID);
  const [nome, setNome] = useState('');
  const [serie, setSerie] = useState(1);
  const [contadorNumero, setContadorNumero] = useState(1);
  const [serieNfe, setSerieNfe] = useState(1);
  const [contadorNumeroNfe, setContadorNumeroNfe] = useState(1);
  const [idTerminalSitef, setIdTerminalSitef] = useState('');
  const [tokenTlsFiserv, setTokenTlsFiserv] = useState('');

  const handleClose = () => {
    setOpen(false);
    props.handleClose();
  };

  function validateSerie() {
    let error = '';
    if (Number.isNaN(serie)) {
      error = 'Campo Obrigatório';
    } else {
      if (serie <= 100 || serie >= 200) {
        error = `A série deve ser um valor de maior que 100 e menor que 200`;
      }
    }
    return error;
  }

  function validateContadorNumero() {
    let error = '';
    if (Number.isNaN(contadorNumero)) {
      error = 'Campo Obrigatório';
    } else {
      if (contadorNumero <= 0 || contadorNumero >= 10000000) {
        error = `A série deve ser um valor de maior que 0 e menor que 10000000`;
      }
    }
    return error;
  }

  function validateSerieNfe() {
    let error = '';
    if (Number.isNaN(serieNfe)) {
      error = 'Campo Obrigatório';
    } else if (serieNfe <= 0 || serieNfe >= 1000) {
      error = `A série deve ser um valor de maior que 0 e menor que 1000`;
    } else if (serieNfe === SERIE_NFE_TESTE) {
      // espelha server/app/Utils/constants.js: SERIE_NFE_TESTE — reservada
      // pra nota de teste de cliente, nunca pode ser usada por um checkout
      error = `A série ${SERIE_NFE_TESTE} é reservada para notas de teste do sistema`;
    }
    return error;
  }

  function validateContadorNumeroNfe() {
    let error = '';
    if (Number.isNaN(contadorNumeroNfe)) {
      error = 'Campo Obrigatório';
    } else {
      if (contadorNumeroNfe <= 0 || contadorNumeroNfe >= 10000000) {
        error = `A série deve ser um valor de maior que 0 e menor que 10000000`;
      }
    }
    return error;
  }

  function isReservedTerminal(terminalId) {
    if (typeof terminalId !== 'string') return false;

    const id = terminalId.trim().toUpperCase();

    // formato esperado: 'SE' seguido de 6 dígitos, exemplo: SE000900
    if (!/^SE\d{6}$/.test(id)) return false;

    // pega a parte numérica (6 dígitos) e converte para número inteiro
    // exemplo: '000900' -> 900
    const numericPart = Number(id.slice(2)); // parseando dígito por dígito internamente

    // faixa reservada: 900 a 999 (corresponde a SE000900..SE000999)
    return numericPart >= 900 && numericPart <= 999;
  }

  function validarPDV(codigo) {
    // Verifica se segue o padrão XXnnnnnn
    //const regex = /^[A-Za-z]{2}\d{6}$/;
    const regex = /^[A-Za-z]{2}\d{6}$/;
    if (!regex.test(codigo)) {
      return {
        valido: false,
        motivo: 'Formato inválido. Use XXnnnnnn (2 letras(CF) + 6 dígitos).',
      };
    }

    if (isReservedTerminal(String(codigo))) {
      return {
        valido: false,
        motivo:
          'Faixa entre SE000900 a SE000999 que é reservada para uso pelo SiTef.',
      };
    }

    // Extrai os números
    const numeros = parseInt(codigo.slice(2), 10);

    // Verifica faixa reservada
    if (numeros >= 900 && numeros <= 999) {
      return {
        valido: false,
        motivo: 'Faixa 000900 a 000999 é reservada para o SiTef.',
      };
    }

    return { valido: true, motivo: 'Código válido.' };
  }

  function validateIdTerminalSitef() {
    let error = '';
    const { motivo, valido } = validarPDV(idTerminalSitef);
    if (!valido) error = motivo;
    return error;
  }

  function validateTokenTlsFiserv() {
    let error = '';
    if (!tokenTlsFiserv) {
      error = 'Campo Obrigatório';
    } else {
      if (tokenTlsFiserv.length > 20) {
        error = 'Este campo deve ter no máximo 20 caracteres';
      }
    }
    return error;
  }

  function getErros() {
    const errosOld = ['', '', '', '', '', ''];
    errosOld[0] = validateSerie();
    errosOld[1] = validateContadorNumero();
    errosOld[2] = validateSerieNfe();
    errosOld[3] = validateContadorNumeroNfe();
    errosOld[4] = validateIdTerminalSitef();
    errosOld[5] = validateTokenTlsFiserv();

    return errosOld;
  }

  const erros = getErros();

  function hasError() {
    for (let i = 0; i < erros.length; i += 1) {
      if (erros[i] !== '') return true;
    }
    return false;
  }

  const erroExistente = hasError();

  async function getDataResource(id) {
    try {
      const data = await getListAllAPI(
        'contas',
        ['id', 'asc'],
        { id: [id] },
        [],
      );
      if (data.data.length > 0) {
        setId(data.data[0].id);
        setNome(data.data[0].nome);
        setSerie(data.data[0].serie_fiscal ? data.data[0].serie_fiscal : 0);
        setContadorNumero(
          data.data[0].contador_numero_fiscal
            ? data.data[0].contador_numero_fiscal
            : 0,
        );
        setSerieNfe(
          data.data[0].serie_fiscal_nfe ? data.data[0].serie_fiscal_nfe : 0,
        );
        setContadorNumeroNfe(
          data.data[0].contador_numero_fiscal_nfe
            ? data.data[0].contador_numero_fiscal_nfe
            : 0,
        );
        setIdTerminalSitef(
          data.data[0].IdTerminalSitef ? data.data[0].IdTerminalSitef : '',
        );
        setTokenTlsFiserv(
          data.data[0].tokenTlsFiserv ? data.data[0].tokenTlsFiserv : '',
        );
        setCarregando(false);
      } else {
        throw 'Este checkout não existe!';
      }
    } catch (erros) {
      enqueueSnackbar(`${erros}`, {
        variant: 'error',
      });
      handleClose();
    }
  }

  useImperativeHandle(ref, () => ({
    handleEdit(id) {
      setOpen(true);
      setCarregando(true);
      setId(id);
      getDataResource(id);
    },
  }));

  async function handleSalvar() {
    if (!erroExistente) {
      setCarregando(true);
      try {
        if (id <= 0) {
          throw 'nenhum caixa selecionado';
        } else {
          const response = await updateAPI('caixas_fc_dados_fiscais', id, {
            serie_fiscal: serie,
            contador_numero_fiscal: contadorNumero,
            serie_fiscal_nfe: serieNfe,
            contador_numero_fiscal_nfe: contadorNumeroNfe,
            IdTerminalSitef: idTerminalSitef,
            tokenTlsFiserv,
          });
          const { status, message } = response.data;
          setCarregando(false);
          if (status) {
            enqueueSnackbar('Dados Fiscais do Caixa Atualizados com Sucesso!', {
              variant: 'success',
            });
            handleClose();
          } else {
            enqueueSnackbar(message, {
              variant: 'error',
            });
          }
        }
      } catch (erros) {
        setCarregando(false);
        enqueueSnackbar(`${erros ? erros.toString() : 'error'}`, {
          variant: 'error',
        });
        handleClose();
      }
    } else {
      enqueueSnackbar('Existem erros no formulário!', {
        variant: 'error',
      });
    }
  }

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        disableBackdropClick
        disableEscapeKeyDown
        scroll="paper"
        //fullScreen
        classes={{ paper: classes.rootDialogo }}
        className={classes.rootDialogo}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="form-dialog-title" onClose={handleClose}>
          {`CHECKOUT: ${nome}`}
        </DialogTitle>
        <DialogContent dividers style={{ height: '100%', padding: '20px' }}>
          {carregando ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                NFC-e
              </Typography>
              <IntegerInput
                handleEnter={() => {}}
                label="Série Fiscal"
                handleKey={(keyCode, keyName) => {}}
                value={Number.isNaN(serie) ? '' : String(serie)}
                onChange={(value) => setSerie(parseInt(value.target.value, 10))}
                error={erros[0] !== ''}
                helperText={erros[0]}
                fullWidth
                autoFocus
              />
              <IntegerInput
                handleEnter={() => {}}
                label="Contador Número Fiscal"
                handleKey={(keyCode, keyName) => {}}
                value={
                  Number.isNaN(contadorNumero) ? '' : String(contadorNumero)
                }
                onChange={(value) =>
                  setContadorNumero(parseInt(value.target.value, 10))
                }
                error={erros[1] !== ''}
                helperText={erros[1]}
                fullWidth
                autoFocus
              />
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 'bold', marginTop: '1em' }}
              >
                NFe
              </Typography>
              <IntegerInput
                handleEnter={() => {}}
                label="Série Fiscal NFe"
                handleKey={(keyCode, keyName) => {}}
                value={Number.isNaN(serieNfe) ? '' : String(serieNfe)}
                onChange={(value) =>
                  setSerieNfe(parseInt(value.target.value, 10))
                }
                error={erros[2] !== ''}
                helperText={erros[2]}
                fullWidth
              />
              <IntegerInput
                handleEnter={() => {}}
                label="Contador Número Fiscal NFe"
                handleKey={(keyCode, keyName) => {}}
                value={
                  Number.isNaN(contadorNumeroNfe)
                    ? ''
                    : String(contadorNumeroNfe)
                }
                onChange={(value) =>
                  setContadorNumeroNfe(parseInt(value.target.value, 10))
                }
                error={erros[3] !== ''}
                helperText={erros[3]}
                fullWidth
              />
              <TextInput
                handleEnter={() => {}}
                label="Id Terminal Sitef"
                handleKey={(keyCode, keyName) => {}}
                value={idTerminalSitef}
                onChange={(value) => setIdTerminalSitef(value.target.value)}
                error={erros[4] !== ''}
                helperText={erros[4]}
                fullWidth
                autoCompleteState="new-password"
              />
              <TextInput
                handleEnter={() => {}}
                label="Token Tls Fiserv"
                handleKey={(keyCode, keyName) => {}}
                value={tokenTlsFiserv}
                onChange={(value) => setTokenTlsFiserv(value.target.value)}
                error={erros[5] !== ''}
                helperText={erros[5]}
                fullWidth
                autoCompleteState="new-password"
              />
            </div>
          )}
        </DialogContent>
        <DialogActions
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button onClick={handleClose} color="primary">
            Fechar
          </Button>
          <div className={classes.wrapper}>
            <Button
              color="primary"
              onClick={() => {
                handleSalvar();
              }}
              disabled={erroExistente || carregando}
              classes={{
                root: classes.btnAtions,
                disabled: classes.btnAtionsDisabled,
              }}
            >
              Cadastrar (F8)
            </Button>
            {carregando && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
});

export default DialogoTrocoInicial;
